/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import {
  AlertBanner,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { getPredefinedComments } from '../../../redux/certdetails/action';
import { DATA_FETCH_ERROR } from '../../../redux/certdetails/types';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox/CertsModalCommentBox';
import { PREDEFINED_COMMENTS_FOR_ACTION } from '../constant';

import invertedMessages from './messages';

import './CertsTerminateModal.css';

function CertsTerminateModal({
  isMobileXlargeWidth,
  autoRevoke,
  commentStatus,
  handleClick,
  handleCancel,
  isLoading,
  isTerminateError,
  setIsTerminateError,
  getDescriptionFlag,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(true);
  const [isCritical, setIsCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');

  const isCommentValid = Boolean(commentValue?.trim().length >= 3);

  const { predefinedComments } = useSelector(
    (state) => state?.certifications?.details?.core
  );
  const { messages } = useSelector((state) => state?.user);
  const l = useInvertedLocalize(invertedMessages);

  const TERMINATE_USER_DATA = {
    DESC: 'CERTS_TERMINATE_DESCRIPTION',
    SUBDESC: 'CERTS_TERMINATE_SUBDESCRIPTION',
  };

  const getDescription = getDescriptionFlag
    ? `${
        messages[TERMINATE_USER_DATA.DESC] ||
        localize(intl, getMessageKey(TERMINATE_USER_DATA.DESC))
      } ${
        messages[TERMINATE_USER_DATA.SUBDESC] ||
        localize(intl, getMessageKey(TERMINATE_USER_DATA.SUBDESC))
      }`
    : messages[TERMINATE_USER_DATA.SUBDESC] ||
      localize(intl, getMessageKey(TERMINATE_USER_DATA.SUBDESC));

  const [commentsDetails, setCommentDetails] = useState({
    commentDescription: '',
    predefComments: [],
    isCommentApplied: false,
    showAlert: false,
  });

  useEffect(() => {
    if (PREDEFINED_COMMENTS_FOR_ACTION.TERMINATE in predefinedComments) {
      setCommentDetails({
        ...commentsDetails,
        predefComments:
          predefinedComments[PREDEFINED_COMMENTS_FOR_ACTION.TERMINATE] || [],
      });
    } else {
      dispatch(
        getPredefinedComments(PREDEFINED_COMMENTS_FOR_ACTION.TERMINATE)
      ).catch((error) => Logger.log(error));
    }
  }, [predefinedComments]);

  const handleCommentText = (text) => {
    setCommentValue(text);
    setCommentDetails({
      ...commentsDetails,
      commentDescription: text,
      showAlert: text.length === 0 ? commentsDetails.showAlert : false,
    });
  };

  const handleSubmit = () => {
    if (!isCommentValid) {
      setIsCritical(true);
      setCommentDetails({
        ...commentsDetails,
        showAlert: true,
      });
    } else {
      let comments = '';

      if (commentsDetails?.commentDescription) {
        comments = commentsDetails.commentDescription;
      }

      handleClick(comments);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsTerminateError(false);
    handleCancel();
  };

  const withoutAutoRevokeWithComments = () => {
    const terminateActionBodyContent = () => (
      <section
        className={classnames(
          isMobileXlargeWidth && 'CertsTerminate-mobileDrawer-modalBackground'
        )}>
        <AlertBanner
          colorTheme='Critical'
          title={DATA_FETCH_ERROR}
          description={
            messages.CERTS_TERMINATE_ERROR ||
            localize(intl, getMessageKey('CERTS_TERMINATE_ERROR'))
          }
          isVisible={isTerminateError}
          onCancel={() => setIsTerminateError(false)}
        />
        <CertsModalCommentBox
          defaultComments={commentsDetails?.predefComments}
          showAlert={isCritical}
          onChange={handleCommentText}
          shouldDisallowBrackets={false}
          label={
            messages['comments.ATTESTATIONS'] ||
            localize(intl, getMessageKey('comments.ATTESTATIONS'))
          }
          dataTestId='Certs-Terminate-commentBoxTestId'
          allowEditPredefinedComments={
            predefinedComments?.allowEditPredefinedComments
          }
        />
      </section>
    );
    const terminateActionFooterContent = () => (
      <FooterActionBar
        size='large'
        className={classnames(
          isMobileXlargeWidth && 'CertsActionPopover-controls'
        )}>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          isDisabled={isLoading}
          onClick={() => {
            setIsOpen(false);
            handleCancel();
          }}>
          {messages.CERTS_TERMINATE_SECONDARY_BTN ||
            localize(intl, getMessageKey('CERTS_TERMINATE_SECONDARY_BTN'))}
        </Button>
        <Button
          type='button'
          kind={isLoading || isCommentValid ? 'filled' : 'outlined'}
          size='medium'
          className={classnames(
            isMobileXlargeWidth && 'CertsActionPopover-submtBtn'
          )}
          isDisabled={isLoading}
          isLoading={isLoading}
          loaderText={
            messages.CERTS_TERMINATE_PRIMARY_BTN ||
            localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))
          }
          onClick={handleSubmit}>
          {messages.CERTS_TERMINATE_PRIMARY_BTN ||
            localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))}
        </Button>
      </FooterActionBar>
    );

    return !isMobileXlargeWidth ? (
      <ModalIsland
        className='CertsTerminateModal-wrapper CertsTerminateModal-withComments'
        onClose={handleClose}
        isOpen={isOpen}
        headerIcon={
          <Icon
            kind='AlertWarningFilledSeparateColors'
            className={classnames(
              `ModalIsland-alertContent-icon CertsTerminateModal-alertIcon${isLoading}` &&
                'CertsTerminateModal-isCrossIconDisabled'
            )}
            size='large'
          />
        }
        title={
          messages.CERTS_TERMINATE_TITLE ||
          localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))
        }
        subtitle={getDescription}
        contentBody={terminateActionBodyContent()}
        FooterActionBarComp={terminateActionFooterContent()}
      />
    ) : (
      <MobileDrawer
        headerContent={
          <header className='CertsTerminate-mobileDrawer-header'>
            <Icon
              kind='AlertWarningFilledSeparateColors'
              className='CertsTerminate-mobileDrawer-warningIcon CertsTerminateModal-isCrossIconDisabled'
              size='medium'
            />
            <div className='CertsTerminate-mobileDrawer-headerText'>
              <Typography kind='h1'>
                {messages.CERTS_TERMINATE_TITLE ||
                  localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))}
              </Typography>
              <Typography kind='body2'>{getDescription}</Typography>
            </div>
            <ButtonIcon
              size='medium'
              kind='ghost'
              ariaLabel={l('Close')}
              icon={
                <Icon kind='close' color='neutral-100' size='smallMedium' />
              }
              onClick={handleClose}
            />
          </header>
        }
        content={
          <>
            {terminateActionBodyContent()}
            {terminateActionFooterContent()}
          </>
        }
        className='CertsTerminate-mobileDrawer'
        setIsOpen={handleClose}
        isOpen={isOpen}
      />
    );
  };

  const withAutoRevokeWithComments = () => {
    const terminateActionBodyContent = () => (
      <section
        className={classnames(
          isMobileXlargeWidth && 'CertsTerminate-mobileDrawer-modalBackground'
        )}>
        <AlertBanner
          colorTheme='Critical'
          title={DATA_FETCH_ERROR}
          description={
            messages.CERTS_TERMINATE_ERROR ||
            localize(intl, getMessageKey('CERTS_TERMINATE_ERROR'))
          }
          isVisible={isTerminateError}
          onCancel={() => setIsTerminateError(false)}
        />
        <CertsModalCommentBox
          defaultComments={commentsDetails?.predefComments}
          showAlert={isCritical}
          onChange={handleCommentText}
          shouldDisallowBrackets={false}
          dataTestId='Certs-Terminate-commentBoxTestId'
          allowEditPredefinedComments={
            predefinedComments?.allowEditPredefinedComments
          }
        />
      </section>
    );
    const terminateActionFooterContent = () => (
      <FooterActionBar
        size='large'
        className={classnames(
          'CertsTerminate-footerContent',
          isMobileXlargeWidth && 'CertsActionPopover-controls'
        )}>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          onClick={() => {
            setIsOpen(false);
            handleCancel();
          }}>
          {messages.CERTS_TERMINATE_SECONDARY_BTN ||
            localize(intl, getMessageKey('CERTS_TERMINATE_SECONDARY_BTN'))}
        </Button>
        <Button
          type='button'
          kind={isLoading || isCommentValid ? 'filled' : 'outlined'}
          size='medium'
          isEnabled
          isDisabled={isLoading}
          isLoading={isLoading}
          loaderText={
            messages.CERTS_TERMINATE_PRIMARY_BTN ||
            localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))
          }
          onClick={handleSubmit}>
          {messages.CERTS_TERMINATE_PRIMARY_BTN ||
            localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))}
        </Button>
      </FooterActionBar>
    );

    return !isMobileXlargeWidth ? (
      <ModalIsland
        className='CertsTerminateModal-wrapper CertsTerminateModal-withComments'
        onClose={handleClose}
        isOpen={isOpen}
        headerIcon={
          <Icon
            kind='AlertCriticalFilledSeparateColors'
            className={classnames(
              'ModalIsland-alertContent-icon',
              'ModalIsland-alertContent-criticalIcon',
              'CertsTerminateModal-alertIcon'
            )}
            size='large'
          />
        }
        title={
          messages.CERTS_TERMINATE_TITLE ||
          localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))
        }
        subtitle={
          <div>
            {getDescription}
            <div className='CertsTerminateModal-NoteWrapper'>
              <b className='CertsTerminateModal-NoteText'>
                {messages.CERTS_TERMINATE_IMPORTANT ||
                  localize(
                    intl,
                    getMessageKey('CERTS_TERMINATE_IMPORTANT')
                  )}{' '}
              </b>
              {messages.CERTS_TERMINATE_NOTE ||
                localize(intl, getMessageKey('CERTS_TERMINATE_NOTE'))}
            </div>
          </div>
        }
        contentBody={terminateActionBodyContent()}
        FooterActionBarComp={terminateActionFooterContent()}
      />
    ) : (
      <MobileDrawer
        content={
          <>
            <header className='CertsTerminate-mobileDrawer-header'>
              <Icon
                kind='AlertCriticalFilledSeparateColors'
                className={classnames(
                  'ModalIsland-alertContent-icon',
                  'ModalIsland-alertContent-criticalIcon',
                  'CertsTerminateModal-alertIcon'
                )}
                size='large'
              />
              <div>
                <Typography kind='h1'>
                  {messages.CERTS_TERMINATE_TITLE ||
                    localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))}
                </Typography>
                <Typography kind='body2'>{getDescription}</Typography>
                <Typography
                  kind='body2'
                  className='CertsTerminateModal-NoteWrapper'>
                  <b className='CertsTerminateModal-NoteText'>
                    {messages.CERTS_TERMINATE_IMPORTANT ||
                      localize(
                        intl,
                        getMessageKey('CERTS_TERMINATE_IMPORTANT')
                      )}{' '}
                  </b>
                  {messages.CERTS_TERMINATE_NOTE ||
                    localize(intl, getMessageKey('CERTS_TERMINATE_NOTE'))}
                </Typography>
              </div>
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Close')}
                icon={
                  <Icon kind='close' color='neutral-100' size='smallMedium' />
                }
                onClick={handleClose}
              />
            </header>
            {terminateActionBodyContent()}
            {terminateActionFooterContent()}
          </>
        }
        className='CertsTerminate-mobileDrawer'
        setIsOpen={handleClose}
        isOpen={isOpen}
      />
    );
  };

  const withoutAutoRevokeNoComments = () => {
    const terminateActionBodyContent = () => (
      <>
        <AlertBanner
          colorTheme='Critical'
          title={DATA_FETCH_ERROR}
          description={
            messages.CERTS_TERMINATE_RETRY ||
            localize(intl, getMessageKey('CERTS_TERMINATE_RETRY'))
          }
          isVisible={isTerminateError}
          onCancel={() => setIsTerminateError(false)}
        />
        {!isMobileXlargeWidth &&
          (messages.CERTS_TERMINATE_NOTE ||
            localize(intl, getMessageKey('CERTS_TERMINATE_NOTE')))}
      </>
    );
    const terminateActionPrimaryButton = () => (
      <Button
        type='button'
        kind={isLoading ? 'filled' : 'outlined'}
        size='medium'
        isDisabled={isLoading}
        isLoading={isLoading}
        loaderText={
          messages.CERTS_TERMINATE_PRIMARY_BTN ||
          localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))
        }
        onClick={handleClick}>
        {messages.CERTS_TERMINATE_PRIMARY_BTN ||
          localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))}
      </Button>
    );
    const terminateActionSecondaryButton = () => (
      <Button
        type='button'
        kind='ghost'
        size='medium'
        onClick={() => {
          setIsOpen(false);
          handleCancel();
        }}>
        {messages.CERTS_TERMINATE_SECONDARY_BTN ||
          localize(intl, getMessageKey('CERTS_TERMINATE_SECONDARY_BTN'))}
      </Button>
    );

    return !isMobileXlargeWidth ? (
      <ModalIsland
        className='CertsTerminateModal-wrapper'
        kind='warning'
        alertTitle={terminateActionBodyContent()}
        alertSubtitle={getDescription}
        onClose={handleClose}
        isOpen={isOpen}
        primaryButton={terminateActionPrimaryButton()}
        secondaryButton={terminateActionSecondaryButton()}
      />
    ) : (
      <MobileDrawer
        content={
          <header className='CertsTerminate-mobileDrawer-header'>
            <ButtonIcon
              size='medium'
              kind='ghost'
              className='CertsTerminate-mobileDrawer-AlertModalCloseButton'
              ariaLabel={l('Close')}
              icon={
                <Icon kind='close' color='neutral-100' size='smallMedium' />
              }
              onClick={handleClose}
            />
            <Icon
              kind='AlertModalBackground'
              color='neutral-100'
              className='ModalIsland-alertBg ModalIsland-alertBg-warning'
            />
            <div className='ModalIsland-alertContent'>
              <Icon
                kind='AlertWarningFilledSeparateColors'
                className='ModalIsland-alertContent-icon'
                dataTestId='ModalIsland-Warning'
              />
              <div className='ModalIsland-alertContent-body'>
                {terminateActionBodyContent()}
                <div>
                  <Typography kind='h1'>
                    {messages.CERTS_TERMINATE_TITLE ||
                      localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))}
                  </Typography>
                  <Typography kind='body2'>{getDescription}</Typography>
                </div>
                {terminateActionPrimaryButton()}
                {terminateActionSecondaryButton()}
              </div>
            </div>
          </header>
        }
        className='CertsTerminate-mobileDrawer'
        setIsOpen={handleClose}
        isOpen={isOpen}
      />
    );
  };

  const withAutoRevokeNoComments = () => {
    const terminateActionBodyContent = () => (
      <>
        <AlertBanner
          colorTheme='Critical'
          title={DATA_FETCH_ERROR}
          description='Please try again.'
          isVisible={isTerminateError}
          onCancel={() => setIsTerminateError(false)}
        />
        {(!isMobileXlargeWidth && messages.CERTS_TERMINATE_TITLE) ||
          localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))}
      </>
    );
    const terminateActionPrimaryButton = () => (
      <Button
        type='button'
        kind={isLoading ? 'filled' : 'outlined'}
        size='medium'
        isDisabled={isLoading}
        isEnabled
        isLoading={isLoading}
        loaderText={
          messages.CERTS_TERMINATE_PRIMARY_BTN ||
          localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))
        }
        onClick={handleClick}>
        {messages.CERTS_TERMINATE_PRIMARY_BTN ||
          localize(intl, getMessageKey('CERTS_TERMINATE_PRIMARY_BTN'))}
      </Button>
    );
    const terminateActionSecondaryButton = () => (
      <Button
        type='button'
        kind='ghost'
        size='medium'
        onClick={() => {
          setIsOpen(false);
          handleCancel();
        }}>
        {messages.CERTS_TERMINATE_SECONDARY_BTN ||
          localize(intl, getMessageKey('CERTS_TERMINATE_SECONDARY_BTN'))}
      </Button>
    );

    return !isMobileXlargeWidth ? (
      <ModalIsland
        className='CertsTerminateModal-wrapper'
        kind='critical'
        alertTitle={terminateActionBodyContent()}
        alertSubtitle={
          <div>
            {getDescription}
            <div className='CertsTerminateModal-NoteWrapper'>
              <b className='CertsTerminateModal-NoteText'>
                {messages.CERTS_TERMINATE_IMPORTANT ||
                  localize(
                    intl,
                    getMessageKey('CERTS_TERMINATE_IMPORTANT')
                  )}{' '}
              </b>
              {messages.CERTS_TERMINATE_NOTE ||
                localize(intl, getMessageKey('CERTS_TERMINATE_NOTE'))}
            </div>
          </div>
        }
        onClose={handleClose}
        isOpen={isOpen}
        primaryButton={terminateActionPrimaryButton()}
        secondaryButton={terminateActionSecondaryButton()}
      />
    ) : (
      <MobileDrawer
        content={
          <header className='CertsTerminate-mobileDrawer-header'>
            <ButtonIcon
              size='medium'
              kind='ghost'
              className='CertsTerminate-mobileDrawer-AlertModalCloseButton'
              ariaLabel={l('Close')}
              icon={
                <Icon kind='close' color='neutral-100' size='smallMedium' />
              }
              onClick={handleClose}
            />
            <Icon
              kind='AlertModalBackground'
              color='neutral-100'
              className='ModalIsland-alertBg ModalIsland-alertBg-critical'
            />
            <div className='ModalIsland-alertContent'>
              <Icon
                kind='AlertCriticalFilledSeparateColors'
                className={classnames(
                  'ModalIsland-alertContent-icon',
                  'ModalIsland-alertContent-criticalIcon',
                  'CertsTerminateModal-alertIcon'
                )}
                dataTestId='ModalIsland-Critical'
              />
              <div className='ModalIsland-alertContent-body'>
                {terminateActionBodyContent()}
                <div>
                  <Typography kind='h1'>
                    {' '}
                    {messages.CERTS_TERMINATE_TITLE ||
                      localize(intl, getMessageKey('CERTS_TERMINATE_TITLE'))}
                  </Typography>
                  <Typography kind='body2'>
                    {getDescription}
                    <div className='CertsTerminateModal-NoteWrapper'>
                      <b className='CertsTerminateModal-NoteText'>
                        {messages.CERTS_TERMINATE_IMPORTANT ||
                          localize(
                            intl,
                            getMessageKey('CERTS_TERMINATE_IMPORTANT')
                          )}
                      </b>
                      {messages.CERTS_TERMINATE_NOTE ||
                        localize(intl, getMessageKey('CERTS_TERMINATE_NOTE'))}
                    </div>
                  </Typography>
                </div>
                {terminateActionPrimaryButton()}
                {terminateActionSecondaryButton()}
              </div>
            </div>
          </header>
        }
        className='CertsTerminate-mobileDrawer'
        setIsOpen={handleClose}
        isOpen={isOpen}
      />
    );
  };

  const getModalContent = () => {
    if (autoRevoke) {
      return commentStatus
        ? withAutoRevokeWithComments()
        : withAutoRevokeNoComments();
    }

    return commentStatus
      ? withoutAutoRevokeWithComments()
      : withoutAutoRevokeNoComments();
  };

  return <section>{getModalContent()}</section>;
}

CertsTerminateModal.defaultProps = { isLoading: null };

CertsTerminateModal.propTypes = {
  autoRevoke: PropTypes.bool.isRequired,
  commentStatus: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isTerminateError: PropTypes.bool.isRequired,
  setIsTerminateError: PropTypes.func.isRequired,
  getDescriptionFlag: PropTypes.bool.isRequired,
  isMobileXlargeWidth: PropTypes.bool.isRequired,
};

export default CertsTerminateModal;
